import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';
import { useChurchPlanner } from '../../hooks/useChurchPlanner';
import { useCurrentUserProfile } from '../../hooks/useCurrentUserProfile';
import { hasAnyPermission, Permission } from '../../utils/roles';
import {
  PlannerCard,
  PlannerError,
  PlannerHeading,
  PlannerKicker,
  PlannerPage,
  PlannerSubtle,
  plannerColors,
} from './plannerTheme';
import { formatSongTemplateLengthDisplay } from './songDuration';

interface SongTemplate {
  id: string;
  title: string;
  artist?: string | null;
  key_signature?: string | null;
  length_minutes: number;
  length_seconds?: number | null;
  bpm?: number | null;
  last_scheduled_date?: string | null;
  sequence: number;
  song_sequence?: string | null;
  video_url?: string | null;
  themes?: string[] | null;
  notes?: string | null;
  is_active: boolean;
}

type SongSyncMeta = {
  sync_state: 'synced' | 'pending' | 'conflict' | 'error' | string;
  last_synced_at?: string | null;
  propresenter_presentation_id?: string | null;
};

const compareSongsByTitle = (a: SongTemplate, b: SongTemplate): number => {
  const titleCompare = (a.title || '').trim().localeCompare((b.title || '').trim(), undefined, {
    sensitivity: 'base',
  });
  if (titleCompare !== 0) return titleCompare;
  return (a.id || '').localeCompare(b.id || '');
};

const pageFontStack = "'Plus Jakarta Sans', 'Avenir Next', 'Segoe UI', sans-serif";

const Panel = styled(PlannerCard)`
  position: relative;
  overflow: hidden;
  border-radius: 20px;
  backdrop-filter: blur(8px);
  border-color: rgba(229, 211, 179, 0.2);
`;

const HeroPanel = styled(Panel)`
  display: grid;
  gap: 0.75rem;
  padding: 1.3rem 1.4rem;
  background:
    radial-gradient(circle at 0% 0%, rgba(229, 211, 179, 0.22), transparent 44%),
    radial-gradient(circle at 100% 100%, rgba(255, 255, 255, 0.08), transparent 40%),
    linear-gradient(132deg, rgba(39, 39, 43, 0.95), rgba(26, 26, 29, 0.98));
`;

const HeroRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  color: ${plannerColors.text};
  font-size: 0.76rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  font-weight: 700;
  font-family: ${pageFontStack};
`;

const SongList = styled.div`
  display: grid;
  gap: 0.8rem;
`;

const SongCard = styled.article`
  border: 1px solid rgba(229, 211, 179, 0.2);
  background:
    radial-gradient(circle at 0% 0%, rgba(229, 211, 179, 0.12), transparent 38%),
    linear-gradient(155deg, rgba(255, 255, 255, 0.05), rgba(255, 255, 255, 0.02));
  border-radius: 16px;
  padding: 0.86rem;
  display: grid;
  grid-template-columns: 74px 1fr;
  gap: 0.8rem;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const SongCover = styled.div`
  width: 74px;
  height: 74px;
  border-radius: 12px;
  border: 1px solid rgba(229, 211, 179, 0.26);
  background:
    radial-gradient(circle at 25% 20%, rgba(229, 211, 179, 0.35), transparent 46%),
    linear-gradient(140deg, #303035 0%, #1f1f22 52%, #17171a 100%);
  color: ${plannerColors.accent};
  font-size: 1.12rem;
  font-weight: 800;
  letter-spacing: 0.07em;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SongBody = styled.div`
  display: grid;
  gap: 0.55rem;
`;

const SongTitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const SongTitleLink = styled(Link)`
  color: ${plannerColors.text};
  font-size: 1.06rem;
  font-family: ${pageFontStack};
  letter-spacing: -0.01em;
  text-decoration: none;
  font-weight: 800;

  &:hover {
    text-decoration: underline;
  }
`;

const SongArtist = styled.p`
  margin: 0;
  color: ${plannerColors.accent};
  font-size: 0.82rem;
  font-weight: 600;
`;

const SongTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`;

const SongTag = styled.span`
  border-radius: 999px;
  border: 1px solid rgba(229, 211, 179, 0.26);
  background: rgba(255, 255, 255, 0.03);
  color: ${plannerColors.text};
  font-size: 0.72rem;
  line-height: 1;
  padding: 0.3rem 0.5rem;
`;

const SyncTag = styled(SongTag)<{ $state: 'synced' | 'pending' | 'conflict' | 'error' | string }>`
  border-color: ${({ $state }) =>
    $state === 'synced'
      ? 'rgba(52, 199, 89, 0.52)'
      : $state === 'pending'
        ? 'rgba(255, 204, 0, 0.52)'
        : 'rgba(255, 69, 58, 0.52)'};
  color: ${({ $state }) =>
    $state === 'synced'
      ? '#d5f5de'
      : $state === 'pending'
        ? '#ffe7a8'
        : '#ffd8d5'};
`;

const SongSequencePreview = styled.p`
  margin: 0;
  color: ${plannerColors.muted};
  font-size: 0.77rem;
  line-height: 1.45;
`;

const Select = styled.select`
  border: 1px solid ${plannerColors.border};
  background: ${plannerColors.surfaceHover};
  color: ${plannerColors.text};
  color-scheme: dark;
  border-radius: 12px;
  padding: 0.55rem 0.68rem;
  appearance: auto;
  width: 100%;

  &:focus {
    outline: none;
    border-color: rgba(229, 211, 179, 0.45);
  }

  &[multiple] {
    min-height: 8.5rem;
    padding: 0.4rem;
  }
`;

const SearchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.55rem;
  flex-wrap: wrap;
  margin-top: 0.55rem;
`;

const SearchInput = styled.input`
  flex: 1;
  min-width: 220px;
  border: 1px solid rgba(229, 211, 179, 0.2);
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.03), rgba(255, 255, 255, 0.012));
  color: ${plannerColors.text};
  border-radius: 12px;
  padding: 0.58rem 0.72rem;
  font-size: 0.84rem;
  font-family: ${pageFontStack};

  &::placeholder {
    color: ${plannerColors.muted};
  }
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  border: 1px solid rgba(241, 226, 198, 0.5);
  background: linear-gradient(140deg, #f1e2c6 0%, #dcc19a 100%);
  color: #171717;
  border-radius: 11px;
  padding: 0.62rem 0.92rem;
  font-size: 0.83rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const GhostButton = styled.button`
  border: 1px solid rgba(229, 211, 179, 0.26);
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.015));
  color: ${plannerColors.text};
  border-radius: 11px;
  padding: 0.52rem 0.8rem;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const AddTemplateLink = styled(Link)`
  border: 1px solid rgba(241, 226, 198, 0.5);
  background: linear-gradient(140deg, #f1e2c6 0%, #dcc19a 100%);
  color: #171717;
  border-radius: 11px;
  padding: 0.6rem 0.9rem;
  font-size: 0.83rem;
  font-weight: 700;
  text-decoration: none;
`;

function getCoverInitials(title: string, artist: string): string {
  const source = (title || artist).trim();
  if (!source) return 'SP';
  const parts = source.split(/\s+/).filter(Boolean);
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function previewSongSequence(value?: string | null): string {
  const normalized = (value || '').trim();
  if (!normalized) return '';
  const parts = normalized
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  if (!parts.length) return '';
  return parts.slice(0, 4).join(' • ');
}

export default function ChurchPlannerSongsPage() {
  const { user } = useAuth();

function formatSongLastScheduledDate(value?: string | null): string {
  if (!value) return 'Not scheduled';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function formatServiceDateLabel(value?: string | null): string {
  if (!value) return 'No date';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
  const planner = useChurchPlanner(Boolean(user), user?.id);
  const navigate = useNavigate();
  const { data: profile } = useCurrentUserProfile(user?.id);
  const roles = profile?.roles ?? user?.user_metadata?.roles;
  const canManagePlanner = hasAnyPermission(roles, [
    Permission.MANAGE_CHURCH_PLANNER_ROLES,
    Permission.MANAGE_CHURCH_PLAN_ASSIGNMENTS,
  ]);

  const [loadingSongs, setLoadingSongs] = useState(false);
  const [applyingSongId, setApplyingSongId] = useState<string | null>(null);
  const [songs, setSongs] = useState<SongTemplate[]>([]);
  const [songSyncMetaBySongId, setSongSyncMetaBySongId] = useState<Record<string, SongSyncMeta>>({});
  const [songSearch, setSongSearch] = useState('');
  const [selectedPlanIds, setSelectedPlanIds] = useState<string[]>([]);
  const [selectedSectionKey, setSelectedSectionKey] = useState('');
  const [errorText, setErrorText] = useState('');

  const plans = planner.upcomingPlans;

  const selectedPlans = useMemo(() => {
    const planById = new Map(plans.map((plan) => [plan.id, plan]));
    return selectedPlanIds
      .map((planId) => planById.get(planId) || null)
      .filter((plan): plan is typeof plans[number] => Boolean(plan));
  }, [plans, selectedPlanIds]);

  const selectedSections = useMemo(() => {
    if (!selectedPlans.length) return [] as Array<{ key: string; label: string }>;

    const sectionMaps = selectedPlans.map((plan) => {
      const map = new Map<string, string>();
      [...(plan.service_plan_sections || [])]
        .sort((a, b) => a.sort_order - b.sort_order)
        .forEach((section) => {
          const key = (section.name || '').trim().toLowerCase();
          if (!key || map.has(key)) return;
          map.set(key, (section.name || '').trim() || 'Section');
        });
      return map;
    });

    const [firstMap, ...remainingMaps] = sectionMaps;
    return Array.from(firstMap.entries())
      .filter(([key]) => remainingMaps.every((map) => map.has(key)))
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
  }, [selectedPlans]);

  const filteredSongs = useMemo(() => {
    const term = songSearch.trim().toLowerCase();
    if (!term) return songs;

    return songs.filter((song) => {
      const title = (song.title || '').toLowerCase();
      const artist = (song.artist || '').toLowerCase();
      const keySignature = (song.key_signature || '').toLowerCase();
      const notes = (song.notes || '').toLowerCase();
      const songSequence = (song.song_sequence || '').toLowerCase();
      const themes = (song.themes || []).join(' ').toLowerCase();
      const sequenceNumber = String(song.sequence ?? '').toLowerCase();

      return (
        title.includes(term)
        || artist.includes(term)
        || keySignature.includes(term)
        || notes.includes(term)
        || songSequence.includes(term)
        || themes.includes(term)
        || sequenceNumber.includes(term)
      );
    });
  }, [songs, songSearch]);

  useEffect(() => {
    if (!plans.length) {
      setSelectedPlanIds([]);
      setSelectedSectionKey('');
      return;
    }
    setSelectedPlanIds((current) => {
      const availableIds = new Set(plans.map((plan) => plan.id));
      const nextSelected = current.filter((planId) => availableIds.has(planId));
      if (nextSelected.length === current.length && nextSelected.every((planId, index) => planId === current[index])) {
        return current;
      }
      if (nextSelected.length) return nextSelected;
      return plans[0]?.id ? [plans[0].id] : [];
    });
  }, [plans]);

  useEffect(() => {
    if (!selectedSections.length) {
      setSelectedSectionKey('');
      return;
    }
    setSelectedSectionKey((current) => {
      if (current && selectedSections.some((section) => section.key === current)) return current;
      return selectedSections[0].key;
    });
  }, [selectedSections]);

  const loadSongs = async () => {
    setLoadingSongs(true);
    setErrorText('');
    const { data, error } = await supabase
      .from('service_song_templates')
      .select('*')
      .eq('is_active', true)
      .order('title', { ascending: true });

    if (error) {
      setErrorText(error.message || 'Unable to load songs.');
      setLoadingSongs(false);
      return;
    }

    setSongs([...((data as SongTemplate[]) ?? [])].sort(compareSongsByTitle));

    const { data: mapRows } = await supabase
      .from('planner_propresenter_song_map')
      .select('song_template_id, sync_state, last_synced_at, propresenter_presentation_id');

    const syncById: Record<string, SongSyncMeta> = {};
    (mapRows || []).forEach((row: any) => {
      const songId = typeof row?.song_template_id === 'string' ? row.song_template_id : '';
      if (!songId) return;
      syncById[songId] = {
        sync_state: row?.sync_state || 'pending',
        last_synced_at: row?.last_synced_at || null,
        propresenter_presentation_id: row?.propresenter_presentation_id || null,
      };
    });
    setSongSyncMetaBySongId(syncById);

    setLoadingSongs(false);
  };

  useEffect(() => {
    void loadSongs();
  }, []);

  const deleteSong = async (songId: string) => {
    setErrorText('');
    const { error } = await supabase.from('service_song_templates').delete().eq('id', songId);
    if (error) {
      setErrorText(error.message || 'Unable to delete song template.');
      return;
    }
    await loadSongs();
  };

  const applySong = async (song: SongTemplate) => {
    if (!selectedPlans.length || !selectedSectionKey) {
      setErrorText('Select one or more services and a shared section first.');
      return;
    }

    const planTargets = selectedPlans.map((plan) => {
      const section = (plan.service_plan_sections || []).find(
        (entry) => (entry.name || '').trim().toLowerCase() === selectedSectionKey
      ) || null;

      return {
        plan,
        section,
      };
    });

    if (planTargets.some((entry) => !entry.section)) {
      setErrorText('One or more selected services no longer has that section.');
      return;
    }

    setApplyingSongId(song.id);
    setErrorText('');
    const results = await Promise.all(
      planTargets.map(async ({ plan, section }) => {
        const sameSectionSubsections = (plan.service_plan_subsections || [])
          .filter((subsection) => subsection.section_id === section!.id)
          .sort((a, b) => a.sort_order - b.sort_order);
        const maxSortOrder = sameSectionSubsections.length
          ? sameSectionSubsections[sameSectionSubsections.length - 1].sort_order
          : -1;

        return supabase.from('service_plan_subsections').insert({
          service_plan_id: plan.id,
          section_id: section!.id,
          song_template_id: song.id,
          name: song.title,
          description: song.notes || null,
          length_minutes: song.length_minutes,
          length_display: formatSongTemplateLengthDisplay(song),
          sort_order: maxSortOrder + 1,
        });
      })
    );
    setApplyingSongId(null);

    const firstError = results.find((result) => result.error)?.error;
    if (firstError) {
      setErrorText(firstError.message || 'Unable to add song to the selected services.');
      return;
    }

    await planner.plansQuery.refetch();
  };

  return (
    <PlannerPage>
      <HeroPanel>
        <HeroRow>
          <div>
            <PlannerKicker>Planner Music</PlannerKicker>
            <PlannerHeading>Songs</PlannerHeading>
            <PlannerSubtle>Manage sons and add to Services.</PlannerSubtle>
          </div>
          <AddTemplateLink to="/church-planner/songs/new">Add Song</AddTemplateLink>
        </HeroRow>


      </HeroPanel>

      {canManagePlanner ? (
        <Panel>
          <PlannerHeading style={{ margin: 0 }}>Add To Service</PlannerHeading>
          <PlannerSubtle>Select one or more services, choose a shared section, then apply templates from the list.</PlannerSubtle>

          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <Label style={{ flex: 1, minWidth: '180px' }}>
              Services
              <Select
                multiple
                size={Math.min(6, Math.max(plans.length, 2))}
                value={selectedPlanIds}
                onChange={(event) => setSelectedPlanIds(Array.from(event.target.selectedOptions, (option) => option.value))}
              >
                {plans.map((plan) => (
                  <option key={plan.id} value={plan.id}>
                    {`${plan.title} (${formatServiceDateLabel(plan.service_date)})`}
                  </option>
                ))}
              </Select>
            </Label>

            <Label style={{ flex: 1, minWidth: '180px' }}>
              Section
              <Select value={selectedSectionKey} onChange={(event) => setSelectedSectionKey(event.target.value)}>
                {selectedSections.map((section) => (
                  <option key={section.key} value={section.key}>
                    {section.label}
                  </option>
                ))}
              </Select>
            </Label>
          </div>
          {plans.length && !selectedSections.length ? (
            <PlannerSubtle>Select services that share the same section name.</PlannerSubtle>
          ) : null}
        </Panel>
      ) : null}

      <Panel>
        <PlannerHeading style={{ margin: 0 }}>Songs</PlannerHeading>
        <SearchRow>
          <SearchInput
            type="search"
            value={songSearch}
            onChange={(event) => setSongSearch(event.target.value)}
            placeholder="Search by title, artist, key, notes, sequence, or theme"
            aria-label="Search songs"
          />
          {songSearch.trim() ? (
            <GhostButton type="button" onClick={() => setSongSearch('')}>
              Clear
            </GhostButton>
          ) : null}
        </SearchRow>
        {!loadingSongs ? (
          <PlannerSubtle>
            Showing {filteredSongs.length}{songSearch.trim() ? ` of ${songs.length}` : ''} song template{filteredSongs.length === 1 ? '' : 's'}.
          </PlannerSubtle>
        ) : null}

        <SongList>
          {loadingSongs ? <PlannerSubtle>Loading songs...</PlannerSubtle> : null}
          {!loadingSongs && songs.length === 0 ? <PlannerSubtle>No song templates yet.</PlannerSubtle> : null}
          {!loadingSongs && songs.length > 0 && filteredSongs.length === 0 ? (
            <PlannerSubtle>No songs match your search.</PlannerSubtle>
          ) : null}
          {filteredSongs.map((song) => (
            (() => {
              const syncState = (songSyncMetaBySongId[song.id]?.sync_state || 'pending').toLowerCase();
              const isMapped = Boolean(songSyncMetaBySongId[song.id]?.propresenter_presentation_id);

              return (
                <SongCard key={song.id}>
                  <SongCover>{getCoverInitials(song.title, song.artist || '')}</SongCover>
                  <SongBody>
                    <SongTitleRow>
                      <div>
                        <SongTitleLink to={`/church-planner/songs/${song.id}`}>{song.title}</SongTitleLink>
                        <SongArtist>{song.artist || 'Unknown artist'}</SongArtist>
                      </div>
                      <ButtonRow>
                        <GhostButton type="button" onClick={() => navigate(`/church-planner/songs/${song.id}`)}>
                          View Song
                        </GhostButton>
                        <GhostButton type="button" onClick={() => void deleteSong(song.id)}>Delete</GhostButton>
                        {canManagePlanner ? (
                          <ActionButton
                            type="button"
                            onClick={() => void applySong(song)}
                            disabled={!selectedPlanIds.length || !selectedSectionKey || applyingSongId === song.id}
                          >
                            {applyingSongId === song.id ? 'Adding...' : 'Add Slot'}
                          </ActionButton>
                        ) : null}
                      </ButtonRow>
                    </SongTitleRow>

                    <SongTags>
                      <SongTag>{formatSongTemplateLengthDisplay(song) || 'No duration'}</SongTag>
                      <SongTag>{song.key_signature || 'No key'}</SongTag>
                      <SongTag>{song.bpm || '--'} BPM</SongTag>
                      <SongTag>Last: {formatSongLastScheduledDate(song.last_scheduled_date)}</SongTag>
                      {syncState !== 'pending' ? <SyncTag $state={syncState}>PP7: {syncState.toUpperCase()}</SyncTag> : null}
                      {isMapped ? <SongTag>Mapped</SongTag> : null}
                    </SongTags>

                    {previewSongSequence(song.song_sequence) ? (
                      <SongSequencePreview>Sequence: {previewSongSequence(song.song_sequence)}</SongSequencePreview>
                    ) : null}
                  </SongBody>
                </SongCard>
              );
            })()
          ))}
        </SongList>
      </Panel>

      {errorText ? <PlannerError>{errorText}</PlannerError> : null}
    </PlannerPage>
  );
}
